import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, TemplateRef, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateLoader, UserService } from '@cfp/core';
import { BuildingDetailsHeader } from '@cfp/features-shared/building';
import { EnergyLabelHistoryList } from '@cfp/features-shared/energy-label-history';
import { NotesList } from '@cfp/features-shared/notes';
import { EnergyLabel } from '@cfp/shared/energy-label';
import { Alert, Icon } from '@cfp/ui';
import { TranslateModule } from '@ngx-translate/core';
import {
  AddressDetailsDTO,
  BuildingDetailsDTO,
  BuildingRegisteredEnergyLabelDTO,
  EnergyLabelTypeDTO,
  LabelGroupDTO,
  NoteDTO,
  OwnershipStatusDTO,
  PortfolioBuildingsRoadmapBuildingDTO,
  PortfolioBuildingsRoadmapBuildingDTORoadmapImplementedMeasures,
  PortfolioBuildingsRoadmapBuildingDTORoadmapPlannedMeasures,
  PortfolioBuildingsRoadmapDTO
} from 'csr-asset-app';
import { EnergyLabelRequirementDTO } from 'csr-energy-label-requirement';
import { map } from 'rxjs';
import {
  ReportBuildingEnergyLabelAlert,
  ReportBuildingImplementedTable,
  ReportBuildingRoadmapTable,
  ReportBuildingsLegend
} from '../../components';
import { ReportPage } from '../report-page';

const TOTAL_PAGE_HEIGHT = 1600;
const BUILDING_HEADER_HEIGHT = 120;
const PAGE_FOOTER_HEIGHT = 40;
const AVAILABLE_PAGE_HEIGHT = TOTAL_PAGE_HEIGHT - BUILDING_HEADER_HEIGHT - PAGE_FOOTER_HEIGHT;
const ALERT_HEIGHT = 150;
const HEADER_HEIGHT = 70;
const NOTE_FIXED_HEIGHT = 80;
const NOTE_LINE_HEIGHT = 27;
const NOTE_CHARACTERS_PER_LINE = 156;
const MEASURE_FIXED_HEIGHT = 144;
const MEASURE_FOOTER_HEIGHT = 44;
const MEASURE_ROW_HEIGHT = 64;
const MIN_MEASURES_ON_PAGE = 3;
const ENERGY_LABEL_HISTORY_ROW_HEIGHT = 100;
const DOCUMENTS_FIXED_HEIGHT = 41;
const DOCUMENTS_ROW_HEIGHT = 44;
/** Legend under planned measures (mt-4 + ~2 icon rows); not reserved during measure packing. */
const PLANNED_MEASURES_LEGEND_HEIGHT = 112;

export type ReportRoadmapBuildingTrailingContentContext = {
  $implicit: PortfolioBuildingsRoadmapBuildingDTO;
  embedded: boolean;
  lastPage: boolean;
};

interface ReportRoadmapBuildingPageData {
  notes: NoteDTO[];
  notesPageNumber: number;
  plannedMeasures: PortfolioBuildingsRoadmapBuildingDTORoadmapPlannedMeasures[];
  plannedPageNumber: number;
  totalInvestments: number[] | undefined;
  implementedMeasures: PortfolioBuildingsRoadmapBuildingDTORoadmapImplementedMeasures[];
  implementedPageNumber: number;
  registeredEnergyLabels: BuildingRegisteredEnergyLabelDTO[];
  documentNames: string[];
  embedTrailingContent: boolean;
}

@Component({
  selector: 'cfp-report-roadmap-building-page',
  templateUrl: 'report-roadmap-building-page.html',
  imports: [
    NgTemplateOutlet,
    ReportPage,
    ReportBuildingRoadmapTable,
    ReportBuildingImplementedTable,
    ReportBuildingsLegend,
    ReportBuildingEnergyLabelAlert,
    BuildingDetailsHeader,
    EnergyLabelHistoryList,
    NotesList,
    EnergyLabel,
    TranslateModule,
    Alert,
    Icon
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportRoadmapBuildingPage {
  protected readonly EnergyLabelTypeDTO = EnergyLabelTypeDTO;
  protected readonly OwnershipStatusDTO = OwnershipStatusDTO;
  protected readonly LabelGroupDTO = LabelGroupDTO;

  public readonly building = input.required<PortfolioBuildingsRoadmapBuildingDTO>();
  public readonly portfolioBuildingsRoadmap = input.required<PortfolioBuildingsRoadmapDTO>();
  public readonly customerName = input.required<string>();
  public readonly translationKeyPrefix = input('portfolioBuildingsRoadmapReport');
  public readonly showEnergyLabelHistory = input(true);
  public readonly headerLogo = input<string | undefined>(undefined);
  public readonly chromeFooter = input<TemplateRef<unknown> | undefined>(undefined);
  /** Optional block (e.g. LBG pathway) after tables; embedded on last page when height fits. */
  public readonly trailingContent = input<TemplateRef<ReportRoadmapBuildingTrailingContentContext> | undefined>(
    undefined
  );
  public readonly trailingContentHeight = input(0);
  /** When true, the last printable sheet for this building is the end of the report (no page-break-after). */
  public readonly endsReport = input(false);

  protected readonly currentEnergyLabel = computed(() => this.building().energyLabels?.currentEnergyLabel);
  protected readonly improvedEnergyLabel = computed(() => this.building().energyLabels?.improvedEnergyLabel);

  protected readonly energyLabelRequirement = computed(
    () => this.building()?.energyLabelRequirement as EnergyLabelRequirementDTO
  );

  protected readonly hasPlannedMeasures = computed(() => {
    const plannedMeasures = this.building().roadmap?.plannedMeasures;
    if (!plannedMeasures) {
      return false;
    }
    return plannedMeasures.length > 0;
  });

  protected readonly hasCalculatedA = computed(() => {
    return (
      this.currentEnergyLabel()?.labelType === EnergyLabelTypeDTO.CALCULATEDCURRENT &&
      this.currentEnergyLabel()?.label[0] === 'A'
    );
  });

  protected readonly buildingDetails = computed(() => {
    const addressDetails: Partial<AddressDetailsDTO> = {
      display: this.building().building.displayAddress,
      monument: this.building().building.monument
    };
    const buildingDetails: Partial<BuildingDetailsDTO> = {
      address: addressDetails as AddressDetailsDTO,
      id: this.building().building.id
    };
    return buildingDetails as BuildingDetailsDTO;
  });

  private createNewPage(page?: Partial<ReportRoadmapBuildingPageData>): ReportRoadmapBuildingPageData {
    return {
      notes: page?.notes ?? [],
      notesPageNumber: page?.notesPageNumber ?? 0,
      plannedMeasures: page?.plannedMeasures ?? [],
      plannedPageNumber: page?.plannedPageNumber ?? 0,
      totalInvestments: page?.totalInvestments ?? undefined,
      implementedMeasures: page?.implementedMeasures ?? [],
      implementedPageNumber: page?.implementedPageNumber ?? 0,
      registeredEnergyLabels: page?.registeredEnergyLabels ?? [],
      documentNames: page?.documentNames ?? [],
      embedTrailingContent: false
    };
  }

  protected readonly pages = computed(() => {
    const allNotes: NoteDTO[] = this.building().notes ?? [];
    const allPlannedMeasures: PortfolioBuildingsRoadmapBuildingDTORoadmapPlannedMeasures[] =
      this.building().roadmap?.plannedMeasures ?? [];
    const allImplementedMeasures: PortfolioBuildingsRoadmapBuildingDTORoadmapImplementedMeasures[] =
      this.building().roadmap?.implementedMeasures ?? [];

    const isPlansToSellBefore2030 =
      this.building().building.ownershipStatus === OwnershipStatusDTO.PLANSTOSELLBEFORE2030;

    if (allNotes.length === 0 && allPlannedMeasures.length === 0 && allImplementedMeasures.length === 0) {
      if (!this.hasCalculatedA() && !isPlansToSellBefore2030) {
        return [];
      }

      let remainingSpace = AVAILABLE_PAGE_HEIGHT;
      if (this.hasCalculatedA()) {
        remainingSpace -= ALERT_HEIGHT;
      }
      if (isPlansToSellBefore2030) {
        remainingSpace -= ALERT_HEIGHT;
      }

      const page = this.createNewPage();
      const trailingHeight = this.trailingContentHeight();
      page.embedTrailingContent = !!this.trailingContent() && trailingHeight > 0 && remainingSpace >= trailingHeight;
      return [page];
    }

    const pages: ReportRoadmapBuildingPageData[] = [];
    let remainingSpace = AVAILABLE_PAGE_HEIGHT;

    if (isPlansToSellBefore2030) {
      remainingSpace -= ALERT_HEIGHT;
    }

    let currentPage: ReportRoadmapBuildingPageData = this.createNewPage();

    // Process notes
    let noteIndex = 0;
    while (noteIndex < allNotes.length) {
      currentPage.notesPageNumber++;
      remainingSpace -= HEADER_HEIGHT;

      while (noteIndex < allNotes.length) {
        const noteSpace =
          NOTE_FIXED_HEIGHT + NOTE_LINE_HEIGHT * Math.ceil(allNotes[noteIndex].text.length / NOTE_CHARACTERS_PER_LINE);
        if (remainingSpace - noteSpace < 0) {
          pages.push(currentPage);
          currentPage = this.createNewPage({ notesPageNumber: currentPage.notesPageNumber });
          remainingSpace = AVAILABLE_PAGE_HEIGHT;
          break;
        }

        currentPage.notes.push(allNotes[noteIndex]);
        noteIndex++;
        remainingSpace -= noteSpace;
      }
    }

    const minSpaceNeededForMeasures = HEADER_HEIGHT + MEASURE_FIXED_HEIGHT + MEASURE_ROW_HEIGHT * MIN_MEASURES_ON_PAGE;
    if (remainingSpace < minSpaceNeededForMeasures) {
      pages.push(currentPage);
      currentPage = this.createNewPage();
      remainingSpace = AVAILABLE_PAGE_HEIGHT;
    }

    // Process planned measures
    let plannedIndex = 0;
    while (plannedIndex < allPlannedMeasures.length) {
      currentPage.plannedPageNumber++;
      remainingSpace -= HEADER_HEIGHT + MEASURE_FIXED_HEIGHT;

      while (plannedIndex < allPlannedMeasures.length) {
        if (remainingSpace - MEASURE_ROW_HEIGHT < 0) {
          pages.push(currentPage);
          currentPage = this.createNewPage({ plannedPageNumber: currentPage.plannedPageNumber });
          remainingSpace = AVAILABLE_PAGE_HEIGHT;
          break;
        }

        currentPage.plannedMeasures.push(allPlannedMeasures[plannedIndex]);
        plannedIndex++;
        remainingSpace -= MEASURE_ROW_HEIGHT;
      }
    }

    // If this is the last index of planned measures
    if (plannedIndex === allPlannedMeasures.length) {
      currentPage.totalInvestments = this.totalInvestments();
      remainingSpace -= MEASURE_FOOTER_HEIGHT;
    }

    if (remainingSpace < minSpaceNeededForMeasures) {
      pages.push(currentPage);
      currentPage = this.createNewPage();
      remainingSpace = AVAILABLE_PAGE_HEIGHT;
    }

    // Process implemented measures
    let implementedIndex = 0;
    while (implementedIndex < allImplementedMeasures.length) {
      currentPage.implementedPageNumber++;
      remainingSpace -= HEADER_HEIGHT + MEASURE_FIXED_HEIGHT;

      while (implementedIndex < allImplementedMeasures.length) {
        if (remainingSpace - MEASURE_ROW_HEIGHT < 0) {
          pages.push(currentPage);
          currentPage = this.createNewPage({
            implementedPageNumber: currentPage.implementedPageNumber
          });
          remainingSpace = AVAILABLE_PAGE_HEIGHT;
          break;
        }

        currentPage.implementedMeasures.push(allImplementedMeasures[implementedIndex]);
        implementedIndex++;
        remainingSpace -= MEASURE_ROW_HEIGHT;
      }
    }

    const registeredEnergyLabels = this.showEnergyLabelHistory() ? (this.building().registeredEnergyLabels ?? []) : [];
    let registedEnergyLabelsSpace = 0;
    if (registeredEnergyLabels.length > 0) {
      registedEnergyLabelsSpace = HEADER_HEIGHT + registeredEnergyLabels.length * ENERGY_LABEL_HISTORY_ROW_HEIGHT;
    }

    const documentNames = this.building().documentNames ?? [];
    let documentNamesSpace = 0;
    if (documentNames.length > 0) {
      documentNamesSpace = HEADER_HEIGHT + DOCUMENTS_FIXED_HEIGHT + documentNames.length * DOCUMENTS_ROW_HEIGHT;
    }

    const neededSpacForRegisteredEnergyLabelsAndDocuments = Math.max(registedEnergyLabelsSpace, documentNamesSpace);

    if (remainingSpace < neededSpacForRegisteredEnergyLabelsAndDocuments) {
      pages.push(currentPage);
      currentPage = this.createNewPage();
      remainingSpace = AVAILABLE_PAGE_HEIGHT;
    }

    currentPage.registeredEnergyLabels = registeredEnergyLabels;
    currentPage.documentNames = documentNames;

    let spaceForTrailing = remainingSpace;
    if (currentPage.plannedMeasures.length > 0) {
      spaceForTrailing -= PLANNED_MEASURES_LEGEND_HEIGHT;
    }

    const trailingHeight = this.trailingContentHeight();
    const canEmbedTrailing = !!this.trailingContent() && trailingHeight > 0 && spaceForTrailing >= trailingHeight;
    currentPage.embedTrailingContent = canEmbedTrailing;

    if (
      pages.length === 0 ||
      currentPage.notes.length > 0 ||
      currentPage.plannedMeasures.length > 0 ||
      currentPage.implementedMeasures.length > 0 ||
      currentPage.registeredEnergyLabels.length > 0 ||
      currentPage.documentNames.length > 0 ||
      canEmbedTrailing
    ) {
      pages.push(currentPage);
    }

    return pages;
  });

  protected readonly trailingContentOnSeparatePage = computed(() => {
    const trailing = this.trailingContent();
    if (!trailing) {
      return false;
    }
    const pages = this.pages();
    if (pages.length === 0) {
      return true;
    }
    return !pages[pages.length - 1].embedTrailingContent;
  });

  protected trailingContentContext(embedded: boolean): ReportRoadmapBuildingTrailingContentContext {
    return {
      $implicit: this.building(),
      embedded,
      // Separate pathway page is last only when this building ends the report.
      lastPage: this.endsReport() && !embedded
    };
  }

  private totalInvestments = computed(() => {
    const plannedMeasures = this.building()?.roadmap?.plannedMeasures;
    if (!plannedMeasures) {
      return [];
    }
    return this.portfolioBuildingsRoadmap().totalInvestmentPerYear.map((totalInvestmentPerYear) =>
      plannedMeasures.reduce(
        (total, plannedMeasure) =>
          (total += plannedMeasure.year === totalInvestmentPerYear.year ? plannedMeasure.investment : 0),
        0
      )
    );
  });

  private readonly userService = inject(UserService);
  private readonly translateLoader = inject(TranslateLoader);

  protected readonly hasBuildingNotesLicense = toSignal(this.userService.hasLicense('building_notes'));

  protected readonly isDocumentsTranslationLoaded = toSignal(
    this.translateLoader.loadTranslation('documents').pipe(map(() => true))
  );

  protected translationKey(suffix: string): string {
    // Content keys always use the shared namespace (LBG overrides via custom i18n).
    // translationKeyPrefix is only for report-page chrome title (buildings vs RETLL).
    return `portfolioBuildingsRoadmapReport.${suffix}`;
  }
}
